import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const alertVariants = cva(
  'relative w-full rounded-lg border p-2 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>sv]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-blue-500',
  {
    variants: {
      variant: {
        default: 'bg-card text-card-foreground border-border',
        destructive:
          'text-destructive bg-destructive/10 border-destructive/20 [&>svg]:text-destructive *:data-[slot=alert-description]:text-destructive/90',
        success:
          'bg-ss-blue-50 border border-ss-blue-500 text-ss-blue-900 [&>svg]:text-ss-blue-700 *:data-[slot=alert-description]:text-ss-blue-800',
        warning:
          'bg-ss-warning-50 border-ss-warning-50 text-ss-neutral-950 [&>svg]:text-ss-neutral-950 [&>svg]:size-4 *:data-[slot=alert-description]:text-ss-neutral-950',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  const isDestructive = variant === 'destructive'

  // Determine appropriate ARIA attributes based on variant
  const ariaLive = isDestructive ? 'assertive' : 'polite'

  return (
    <div
      data-slot="alert"
      role="alert"
      aria-live={ariaLive}
      aria-atomic="true"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  )
}

function AlertTitle({
  className,
  children,
  ...props
}: React.ComponentProps<'h5'>) {
  return (
    <h5
      data-slot="alert-title"
      className={cn(
        'col-start-2 line-clamp-1 min-h-4 tracking-tight text-sm font-normal',
        className
      )}
      {...props}
    >
      {children}
    </h5>
  )
}

function AlertDescription({
  className,
  children,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'text-muted-foreground col-start-2 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export { Alert, AlertTitle, AlertDescription }
